/**
 * TTD tab — seven reports, one per collector endpoint (see
 * mirrors-collector's collector/api/ttd_metrics.py and api/schemas.ts's own
 * module comment): the ttd_asset and ttd_iris_asset pipeline stage counts,
 * per-source ingestion from ttd_source_data, and four ClickHouse ctv_stats
 * views (daily traffic, matched-by daily, matched-by buckets, and the
 * overall summary). Each is its own `Tile`; `Section` headers live in
 * App.tsx.
 */
import {
  useTtdAssetPipeline, useTtdCtvDaily, useTtdCtvMatchedbyBuckets, useTtdCtvMatchedbyDaily,
  useTtdCtvStats, useTtdIrisAssetPipeline, useTtdSourceData,
} from '../api/queries'
import { useRange, rangeLabel } from '../api/range'
import { GroupedBars, StackedBar } from '../components/charts'
import { PanelState, Tile } from '../components/ui'
import { n, pct } from '../components/format'

function StageCountsTable({ counts }: { counts: { stage: string; count: number }[] }) {
  const total = counts.reduce((s, c) => s + c.count, 0)
  return (
    <table className="t">
      <thead>
        <tr><th>stage</th><th className="n">count</th><th className="n">share</th></tr>
      </thead>
      <tbody>
        {counts.map((c) => (
          <tr key={c.stage}>
            <td>{c.stage.replace(/_/g, ' ')}</td>
            <td className="n">{n(c.count)}</td>
            <td className="n q">{pct(total ? (c.count / total) * 100 : 0)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function TtdAssetPipeline() {
  const { data, isLoading, error } = useTtdAssetPipeline()
  const { days } = useRange()
  return (
    <Tile title="TTD · ttd_asset pipeline"
          hint={`ttd_asset · Postgres · trailing ${rangeLabel(days)}${data ? ` · ${n(data.total_assets)} assets total` : ''}`}>
      <PanelState isLoading={isLoading} error={error}>
        {!data || data.counts.length === 0
          ? <p className="panel-load">no data</p>
          : <StageCountsTable counts={data.counts} />}
      </PanelState>
    </Tile>
  )
}

/** Not rendered on the board (removed at the user's request — every row was
 * reading 0 for every stage: IRIS-sourced assets aren't yet flowing through
 * this pipeline in a way that shows up here). Kept as an API-level report
 * (collector/api/ttd_metrics.py's /iris-asset-pipeline) in case that changes,
 * the same way TtdCtvMatchedbyDaily above is kept without being surfaced. */
export function TtdIrisAssetPipeline() {
  const { data, isLoading, error } = useTtdIrisAssetPipeline()
  const { days } = useRange()
  return (
    <Tile title="TTD · ttd_iris_asset pipeline" hint={`ttd_iris_asset · Postgres · trailing ${rangeLabel(days)}`}>
      <PanelState isLoading={isLoading} error={error}>
        {!data || data.counts.length === 0
          ? <p className="panel-load">no data</p>
          : <StageCountsTable counts={data.counts} />}
      </PanelState>
    </Tile>
  )
}

export function TtdSourceData() {
  const { data, isLoading, error } = useTtdSourceData()
  const { days } = useRange()
  const sources = data?.sources ?? []
  return (
    <Tile title="TTD · source ingestion & classification"
          hint={`ttd_source_data · Postgres · per source · trailing ${rangeLabel(days)}`}>
      <PanelState isLoading={isLoading} error={error}>
        {sources.length === 0 ? <p className="panel-load">no data</p> : (
          <table className="t">
            <thead>
              <tr>
                <th>source</th><th className="n">total</th><th className="n">done</th>
                <th className="n">pending</th><th className="n">identification failed</th>
                <th className="n">categorized</th><th className="n">not categorized</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.source}>
                  <td>{s.source}</td>
                  <td className="n">{n(s.total)}</td>
                  <td className="n" style={{ color: 'var(--ok)' }}>{n(s.done)}</td>
                  <td className="n q">{n(s.pending)}</td>
                  <td className="n" style={{ color: 'var(--warning)' }}>{n(s.identification_failed)}</td>
                  <td className="n" style={{ color: 'var(--ok)' }}>{n(s.categorized)}</td>
                  <td className="n q">{n(s.not_categorized)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PanelState>
    </Tile>
  )
}

export function TtdCtvDaily() {
  const { data, isLoading, error } = useTtdCtvDaily()
  const { days: rangeDays } = useRange()
  const days = data?.days ?? []
  return (
    <Tile title="TTD · CTV daily traffic"
          hint={`ctv_stats · ClickHouse · matched vs not matched, per day · trailing ${rangeLabel(rangeDays)}`}>
      <PanelState isLoading={isLoading} error={error}>
        {days.length === 0 ? <p className="panel-load">no data</p> : (
          <GroupedBars
            label="CTV daily traffic: matched vs not matched"
            days={days.map((d) => d.date)}
            a={days.map((d) => d.success)}
            b={days.map((d) => d.failure)}
            aLabel="matched" bLabel="not matched"
            tooltip={(i) => {
              const d = days[i]!
              const total = d.success + d.failure
              const sharePct = (v: number) => (total ? pct((v / total) * 100) : pct(0))
              return `${d.date} · matched ${n(d.success)} (${sharePct(d.success)}) · `
                + `not matched ${n(d.failure)} (${sharePct(d.failure)})`
            }}
          />
        )}
      </PanelState>
    </Tile>
  )
}

/** Raw per-day, per-strategy breakdown (dozens of rows per day) — kept as an
 * API-level report (see collector/api/ttd_metrics.py's /ctv-matchedby-daily)
 * but not surfaced in the default Matched-by section, which shows the
 * rolled-up genre/content id/series view instead (TtdCtvMatchedbyBuckets):
 * a full raw table made that section unusably long for a glance-level board. */
export function TtdCtvMatchedbyDaily() {
  const { data, isLoading, error } = useTtdCtvMatchedbyDaily()
  const { days: rangeDays } = useRange()
  const entries = data?.entries ?? []
  return (
    <Tile title="TTD · matched-by, daily"
          hint={`ctv_stats · ClickHouse · which match strategy, per day · trailing ${rangeLabel(rangeDays)}`}>
      <PanelState isLoading={isLoading} error={error}>
        {entries.length === 0 ? <p className="panel-load">no data</p> : (
          <table className="t">
            <thead>
              <tr><th>date</th><th>matched by</th><th className="n">total</th></tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={`${e.date}-${e.matchedby}`}>
                  <td className="q">{e.date}</td>
                  <td>{e.matchedby}</td>
                  <td className="n">{n(e.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PanelState>
    </Tile>
  )
}

// The five named match-strategy prefixes the collector reports (see
// collector/api/ttd_metrics.py's MATCHEDBY_BUCKETS); anything else --
// ratings (R_) and any unrecognised prefix -- rolls into "other" here so the
// percentages still sum to the section's whole.
const DISPLAY_BUCKETS = ['genre', 'contentid', 'series', 'title_series', 'channel_genre'] as const
const BUCKET_TITLE: Record<string, string> = {
  contentid: 'content id', title_series: 'title series', channel_genre: 'channel genre',
}

export function TtdCtvMatchedbyBuckets() {
  const { data, isLoading, error } = useTtdCtvMatchedbyBuckets()
  const { days } = useRange()
  const buckets = data?.buckets ?? []
  const total = buckets.reduce((s, b) => s + b.total, 0)
  const byLabel = Object.fromEntries(buckets.map((b) => [b.label, b.total]))
  const shown = DISPLAY_BUCKETS.map((label) => ({ label, total: byLabel[label] ?? 0 }))
  const otherTotal = total - shown.reduce((s, b) => s + b.total, 0)
  const rows = [...shown, { label: 'other', total: otherTotal }]

  return (
    <Tile title="TTD · matched by category"
          hint={`ctv_stats · ClickHouse · genre / content id / series / title series / channel genre · trailing ${rangeLabel(days)}`}>
      <PanelState isLoading={isLoading} error={error}>
        {total === 0 ? <p className="panel-load">no data</p> : (
          <table className="t">
            <thead>
              <tr><th>category</th><th className="n">count</th><th className="n">share of total</th></tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.label}>
                  <td>{BUCKET_TITLE[b.label] ?? b.label}</td>
                  <td className="n">{n(b.total)}</td>
                  <td className="n q">{pct(total ? (b.total / total) * 100 : 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PanelState>
    </Tile>
  )
}

export function TtdCtvStats() {
  const { data, isLoading, error } = useTtdCtvStats()
  const { days } = useRange()
  return (
    <>
      <Tile title="TTD · CTV stats — summary" hint={`ctv_stats · ClickHouse · trailing ${rangeLabel(days)}`}>
        <PanelState isLoading={isLoading} error={error}>
          {!data || data.summary.total_rows === 0 ? <p className="panel-load">no data</p> : (
            <>
              <div className="cov-head">
                <div className={`cov-pct tnum mono ${data.summary.success_rate >= 90 ? 'ok' : 'warn'}`}>
                  {data.summary.success_rate.toFixed(1)}<i>%</i>
                </div>
                <div className="cov-pct-l">matched<br />success rate</div>
              </div>
              <StackedBar
                label={`${n(data.summary.success_count)} matched of ${n(data.summary.total_rows)} content ids`}
                segments={[
                  { name: 'matched', value: data.summary.success_count, state: 'ok' },
                  { name: 'unmatched', value: data.summary.failure_count, state: 'warn' },
                ]}
              />
              <table className="t" style={{ marginTop: 14 }}>
                <tbody>
                  <tr><td>unique content ids</td><td className="n">{n(data.summary.unique_content_ids)}</td></tr>
                  <tr><td>IRIS-sourced</td><td className="n">{n(data.summary.iris_count)}</td></tr>
                  <tr><td>non-IRIS</td><td className="n">{n(data.summary.non_iris_count)}</td></tr>
                </tbody>
              </table>
            </>
          )}
        </PanelState>
      </Tile>

      <Tile title="TTD · CTV stats — matched-by breakdown" hint="which raw match strategy resolved each content id">
        <PanelState isLoading={isLoading} error={error}>
          {!data || data.matchedby_breakdown.length === 0 ? <p className="panel-load">no data</p> : (
            <table className="t">
              <thead>
                <tr><th>matched by</th><th className="n">success</th><th className="n">failure</th><th className="n">total</th></tr>
              </thead>
              <tbody>
                {data.matchedby_breakdown.map((m) => (
                  <tr key={m.matchedby}>
                    <td>{m.matchedby}</td>
                    <td className="n">{n(m.success_count)}</td>
                    <td className="n q">{n(m.failure_count)}</td>
                    <td className="n">{n(m.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </PanelState>
      </Tile>

      <Tile title="TTD · CTV stats — top failing URLs" hint={`most content-id match failures, trailing ${rangeLabel(days)}`}>
        <PanelState isLoading={isLoading} error={error}>
          {!data || data.top_failing_urls.length === 0 ? <p className="panel-load">no data</p> : (
            <table className="t">
              <thead>
                <tr><th>url</th><th className="n">failures</th><th className="n">success</th><th className="n">total</th></tr>
              </thead>
              <tbody>
                {data.top_failing_urls.map((u) => (
                  <tr key={u.url}>
                    <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={u.url}>{u.url}</td>
                    <td className="n" style={{ color: 'var(--warning)' }}>{n(u.failure_count)}</td>
                    <td className="n">{n(u.success_count)}</td>
                    <td className="n">{n(u.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </PanelState>
      </Tile>
    </>
  )
}
